// @ts-check
/**
 * Made-to-order details on an order: the promised dispatch date and each piece's customer measurements
 * and fit note. Read-only until Edit is pressed; saved through the production-details endpoint.
 */
import { useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminOrderService from '../services/adminOrderService';
import { MEASUREMENT_FIELDS, cleanMeasurements, validateMeasurements } from '../utils/customerMeasurements';
import { isDispatchOverdue } from '../utils/dispatch';
import { formatDay, todayInIndia } from '../utils/salesEvents';
import MeasurementInputs from './MeasurementInputs';

const asText = (measurements) =>
  Object.fromEntries(Object.entries(measurements || {}).map(([key, value]) => [key, String(value)]));

const summary = (measurements) =>
  MEASUREMENT_FIELDS.filter(({ key }) => measurements?.[key] != null)
    .map(({ key, label }) => `${label} ${measurements[key]}`)
    .join(' · ');

/** @param {{order: any, onSaved: () => void}} props */
const OrderProductionDetails = ({ order, onSaved }) => {
  const toast = useToast();
  const items = order.items || [];
  const hasDetails = Boolean(order.promised_dispatch_date) || items.some((i) => i.custom_measurements || i.fit_notes);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(order.promised_dispatch_date || '');
  const [pieces, setPieces] = useState(() =>
    Object.fromEntries(
      items.map((i) => [i.id, { measurements: asText(i.custom_measurements), fitNotes: i.fit_notes || '' }]),
    ),
  );

  const overdue = isDispatchOverdue(order.promised_dispatch_date, order.order_status, todayInIndia());

  const save = async () => {
    for (const item of items) {
      const problem = validateMeasurements(pieces[item.id]?.measurements);
      if (problem) {
        toast.error(`${item.product_name}: ${problem}`);
        return;
      }
    }
    setSaving(true);
    try {
      await adminOrderService.updateProductionDetails(order.id, {
        promised_dispatch_date: date || null,
        items: items.map((item) => ({
          item_id: item.id,
          measurements: cleanMeasurements(pieces[item.id]?.measurements),
          fit_notes: pieces[item.id]?.fitNotes.trim() || null,
        })),
      });
      toast.success('Made-to-order details saved');
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Failed to save the details');
    } finally {
      setSaving(false);
    }
  };

  if (!hasDetails && !editing) {
    return (
      <button onClick={() => setEditing(true)} className="text-sm text-muted-foreground underline">
        + Add dispatch date or measurements
      </button>
    );
  }

  if (!editing) {
    return (
      <div className="border rounded-lg border-border p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-lg font-medium">Made-to-order details</h3>
          <button
            onClick={() => setEditing(true)}
            className="text-sm px-3 py-1 border border-input rounded-lg hover:bg-muted/50"
          >
            Edit
          </button>
        </div>
        <p className="text-sm mb-3">
          Dispatch by:{' '}
          {order.promised_dispatch_date ? (
            <span className={overdue ? 'font-semibold text-red-600 dark:text-red-400' : 'font-medium'}>
              {formatDay(order.promised_dispatch_date)}
              {overdue && ' (overdue)'}
            </span>
          ) : (
            <span className="text-muted-foreground">not set</span>
          )}
        </p>
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="text-sm">
              <p className="font-medium">
                {item.product_name} &mdash; Size {item.size}
              </p>
              <p className="text-muted-foreground">{summary(item.custom_measurements) || 'No measurements taken'}</p>
              {item.fit_notes && <p className="text-muted-foreground">Fit note: {item.fit_notes}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg border-border p-4 space-y-4">
      <h3 className="text-lg font-medium">Made-to-order details</h3>
      <label className="block text-sm">
        Dispatch by
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="block mt-1 px-3 py-2 border border-input rounded-lg"
        />
        {date && (
          <button onClick={() => setDate('')} className="text-xs text-muted-foreground underline mt-1">
            Clear the date
          </button>
        )}
      </label>
      {items.map((item) => (
        <div key={item.id}>
          <p className="text-sm font-medium mb-2">
            {item.product_name} &mdash; Size {item.size}
          </p>
          <MeasurementInputs
            measurements={pieces[item.id]?.measurements}
            fitNotes={pieces[item.id]?.fitNotes}
            onChange={(patch) => setPieces((prev) => ({ ...prev, [item.id]: { ...prev[item.id], ...patch } }))}
          />
        </div>
      ))}
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-muted-foreground/50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={() => setEditing(false)}
          disabled={saving}
          className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default OrderProductionDetails;
